import { Link } from 'react-router-dom';
import { useTicks } from '../data/DataProvider';
import { useSession } from '../store/session';
import { useContractMeta, useLivePortfolio, useMarketStatus } from '../data/hooks';
import { useWatchlist } from '../store/watchlist';
import { istClock, rupee } from '../lib/format';
import { Card, PageHead } from '../ui/common';
import { QuoteRow } from '../ui/pro';
import { Reveal, Stagger, StaggerItem } from '../ui/motion';
import { IconChevronRight, IconWallet } from '../ui/icons';

/**
 * The desk.
 *
 * This screen used to be three cards on a lot of whitespace, which is the
 * wrong shape for the first thing a trader opens: it made a funded account
 * and an empty one look about the same. It is now laid out the way a dealing
 * screen is — benchmarks across the top, the account's own numbers in one
 * hairline strip under them, and then the two columns a trading day is
 * actually spent in: what you are holding and working on the left, what you
 * are watching on the right.
 *
 * Nothing here invents market-wide data. Movers are ranked from the symbols
 * the account follows and labelled as such, and the starter list below is
 * explicitly a starting point rather than a "top picks" list, because the API
 * exposes no full-universe scan and a hardcoded basket dressed up as one
 * would be a worse lie than saying what the scope is.
 */


/** Index heavyweights, offered as a place to start — never as a suggestion. */
const STARTERS = [
  'RELIANCE',
  'HDFCBANK',
  'TCS',
  'INFY',
  'ICICIBANK',
  'SBIN',
  'BHARTIARTL',
  'ITC',
];

/**
 * How the book is doing, in one line.
 *
 * This was the full four-figure readout — the same block Portfolio opens
 * with. Repeating it here made the home page a second Portfolio rather than
 * a home page, and left a trader wanting the actual portfolio unsure which
 * of the two to open.
 *
 * A glance is still worth having: this is the first screen after signing in,
 * and "how am I doing" is the question it should answer. So the figures stay
 * as a single strip that LINKS to Portfolio rather than a block competing
 * with it. One line, three numbers, one destination.
 */
function AccountStrip() {
  const { data, isPending } = useLivePortfolio();

  if (isPending || !data) return <div className="skeleton mb-3" style={{ height: 44 }} />;

  const tone = data.dayPnl > 0 ? 'up' : data.dayPnl < 0 ? 'down' : '';

  return (
    <Link to="/portfolio" className="acct-strip">
      <span className="acct-item">
        <span className="label">Day&rsquo;s P&L</span>
        <span className={`v num ${tone}`}>{rupee(data.dayPnl, { sign: true })}</span>
      </span>
      <span className="acct-item">
        <span className="label">Current value</span>
        <span className="v num">{rupee(data.current)}</span>
      </span>
      <span className="acct-item">
        <span className="label">Funds</span>
        <span className="v num">{rupee(data.availableFunds)}</span>
      </span>
      <IconChevronRight size={16} />
    </Link>
  );
}

/** Movers, ranked strictly from what this account follows. */
/**
 * What the things you follow are doing, in one ranked list.
 *
 * This was two cards side by side — "Gainers you follow" and "Losers you
 * follow" — which meant a watchlist where one name was down rendered a
 * full-height panel holding a single row beside a full one, and a flat
 * market rendered two empty-state sentences. The split cost a box, a header
 * and a column of dead space to express something one sorted list says
 * better: biggest riser at the top, biggest faller at the bottom, and the
 * sign of each number already tells you which is which.
 */
function MoversPanel({ symbols }: { symbols: string[] }) {
  const ticks = useTicks(symbols);
  const meta = useContractMeta(symbols);

  const ranked = symbols
    .map((s) => ({ symbol: s, tick: ticks[s] }))
    .filter((r): r is { symbol: string; tick: NonNullable<(typeof r)['tick']> } => r.tick != null)
    .filter((r) => r.tick.changePct !== 0)
    .sort((a, b) => b.tick.changePct - a.tick.changePct);

  if (ranked.length === 0) return null;

  // Ends of the range, not the middle: six rows of "roughly flat" is what a
  // movers list exists to skip past.
  const shown = ranked.length > 8 ? [...ranked.slice(0, 4), ...ranked.slice(-4)] : ranked;

  return (
    <Card tight title="Movers you follow">
      {shown.map((r) => (
        <QuoteRow key={r.symbol} symbol={r.symbol} tick={r.tick} meta={meta[r.symbol]} />
      ))}
    </Card>
  );
}

/**
 * A place to start when the watchlist is thin. Labelled for what it is: the
 * heaviest names in the index, not a recommendation and not a movers list.
 */
function StarterPanel() {
  const watch = useWatchlist();
  const symbols = STARTERS.filter((s) => !watch.symbols.includes(s)).slice(0, 6);
  const ticks = useTicks(symbols);
  const meta = useContractMeta(symbols);
  if (symbols.length === 0) return null;

  return (
    <Card
      tight
      title="Index heavyweights"
      action={<span className="q-sub">a starting point, not advice</span>}
    >
      <div className="q-cols">
        {symbols.map((s) => (
          <QuoteRow
            key={s}
            symbol={s}
            tick={ticks[s]}
            meta={meta[s]}
            watched={false}
            onToggleWatch={() => watch.add(s)}
          />
        ))}
      </div>
    </Card>
  );
}

/** The signed-out pitch, kept to one hairline band rather than a whole card. */
function SignInBand() {
  return (
    <div
      className="card between wrap ruled"
      style={{ gap: 14, padding: '16px 18px', borderRadius: 'var(--r-lg)' }}
    >
      <div style={{ minWidth: 0 }}>
        <div className="t-lg semibold">Trade this book, not a copy of it</div>
        <div className="t-md muted mt-1">
          Prices above are live. Placing an order, adding funds and pulling a report need a
          verified number — it takes a code and about twenty seconds.
        </div>
      </div>
      <div className="row" style={{ gap: 10 }}>
        <Link to="/" className="btn">
          Why Dabba
        </Link>
        <Link to="/login" className="btn btn-primary">
          Open your book
        </Link>
      </div>
    </div>
  );
}

export default function ExplorePage() {
  const market = useMarketStatus();
  const token = useSession((s) => s.token);
  const watched = useWatchlist((s) => s.symbols);

  return (
    <div className="page">
      <PageHead
        title="Desk"
        sub={market ? `${market.label} · ${istClock(market.serverTimeIst)} IST` : undefined}
        action={
          token ? (
            <Link to="/wallet" className="btn btn-primary row">
              <IconWallet size={16} /> Add funds
            </Link>
          ) : null
        }
      />

      {/*
        There is no Benchmarks strip here any more. The tape above the page
        already prints NIFTY, BANK NIFTY, SENSEX, FIN SERVICE and INDIA VIX,
        live — a second row of cards restated all five, one screenful lower,
        and pushed the account's own numbers below the fold. The tape is the
        canonical place for an index; this page is for the account.
      */}

      {token ? (
        <Reveal y={8}>
          <AccountStrip />
        </Reveal>
      ) : (
        <Reveal y={8}>
          <SignInBand />
        </Reveal>
      )}

      {/*
        What is NOT on this page any more, and why.

        Explore sits beside Watchlist, Portfolio, Orders and Funds in the nav,
        and it used to render a panel for three of them: the account readout
        and open positions (Portfolio), working orders (Orders), and the
        watchlist itself (Watchlist). Opening the app therefore showed the
        same tables as whichever tab you were about to open, which makes the
        tab bar meaningless — every route led somewhere you had just been.

        What is left is what only this page does. Movers ranks what you follow
        by what it is doing RIGHT NOW, which the watchlist — ordered by what
        you added, and by design not re-sorting under you — cannot tell you.
        Heavyweights is a way in when you follow nothing yet. The book is one
        tap away through the strip above.
      */}
      <Stagger className="col" style={{ gap: 18 }} gap={0.06}>
        <StaggerItem>
          <MoversPanel symbols={watched} />
        </StaggerItem>
        <StaggerItem>
          <StarterPanel />
        </StaggerItem>
      </Stagger>
    </div>
  );
}

